import React from "react";
import { Link } from "react-router-dom";
import { Waves, Sun, Cloud, Wind, ArrowRight, Activity, TrendingUp, Minus } from "lucide-react";
import PageHero from "../components/PageHeader";
import { HOME_WATERS, PAGE_HEROES } from "../data/site";

function WeatherIcon({ kind }) {
  const cls = "text-[#b8895a]";
  if (kind === "cloud-sun") return <Cloud size={22} className={cls} />;
  if (kind === "wind") return <Wind size={22} className={cls} />;
  return <Sun size={22} className={cls} />;
}

function FlowTrend({ kind }) {
  if (kind === "lake") return <Waves size={12} />;
  if (kind === "level") return <Minus size={12} />;
  return <TrendingUp size={12} className="rotate-45" />;
}

function ReportDot({ tone }) {
  const colors = {
    good: "bg-emerald-500",
    fair: "bg-amber-500",
    poor: "bg-red-500",
  };
  return <span className={`inline-block w-2.5 h-2.5 rounded-full ${colors[tone] || colors.good}`} />;
}

function RiverCard({ water, index }) {
  return (
    <article
      data-testid={`river-card-${index}`}
      className="surface-card rounded-md overflow-hidden flex flex-col rise-in"
      style={{ animationDelay: `${0.06 * index}s` }}
    >
      <div className="img-frame aspect-[4/3]">
        <img src={water.image} alt={water.name} loading="lazy" />
      </div>
      <div className="p-6 md:p-7 flex-1 flex flex-col">
        <h3
          data-testid={`river-card-${index}-name`}
          className="font-serif text-2xl md:text-[1.7rem] text-center text-[#13263c] uppercase tracking-[0.08em]"
        >
          {water.name}
        </h3>

        {/* Stats panel */}
        <div className="surface-card-soft rounded-md mt-5 px-4 py-4 grid grid-cols-3 gap-3 text-center">
          <div>
            <p className="text-[0.62rem] tracking-[0.22em] uppercase text-[#737373] font-semibold">Flow</p>
            <div className="flex items-center justify-center gap-2 mt-2">
              <Activity size={18} className="text-[#1a3a5c]" />
              <span className="font-serif text-base md:text-lg text-[#13263c]">{water.flow}</span>
            </div>
            <p className="mt-1 text-[0.6rem] tracking-wide text-[#737373] flex items-center justify-center gap-1">
              <FlowTrend kind={water.flowTrend} />
              <span>{water.flowNote}</span>
            </p>
          </div>
          <div className="border-x border-[#ddd4c0] px-1">
            <p className="text-[0.62rem] tracking-[0.22em] uppercase text-[#737373] font-semibold">Weather</p>
            <div className="flex items-center justify-center gap-2 mt-2">
              <WeatherIcon kind={water.weatherIcon} />
              <span className="font-serif text-base md:text-lg text-[#13263c]">{water.weather}</span>
            </div>
            <p className="mt-1 text-[0.6rem] tracking-wide text-[#737373]">{water.weatherNote}</p>
          </div>
          <div>
            <p className="text-[0.62rem] tracking-[0.22em] uppercase text-[#737373] font-semibold">Report</p>
            <div className="flex items-center justify-center gap-2 mt-2">
              <ReportDot tone={water.reportTone} />
              <span className="font-serif text-base md:text-lg text-[#13263c]">{water.report}</span>
            </div>
            <p className="mt-1 text-[0.6rem] tracking-wide text-[#737373]">&nbsp;</p>
          </div>
        </div>

        {/* Outbound TroutRoutes link */}
        <a
          href={water.troutRoutes}
          target="_blank"
          rel="noreferrer noopener"
          data-testid={`river-card-${index}-troutroutes`}
          className="mt-5 pt-5 border-t border-[#e3ddcf] flex items-center justify-between gap-3 group"
        >
          <span className="flex items-center gap-2 text-[#13263c]">
            <Waves size={16} className="text-[#1a3a5c]" />
            <span className="font-medium tracking-wide text-sm">TroutRoutes</span>
          </span>
          <span className="text-[#4a4a4a] text-sm flex-1 truncate">{water.note}</span>
          <ArrowRight size={18} className="text-[#13263c] group-hover:text-[#b8895a] group-hover:translate-x-0.5 transition-all" />
        </a>
      </div>
    </article>
  );
}

export default function HomeWaters() {
  const hero = PAGE_HEROES.homeWaters;
  return (
    <div data-testid="home-waters-page" className="bg-[#f7f4ec]">
      <PageHero image={hero.image} title={hero.title} subtitle={hero.subtitle} testId="home-waters-hero" />

      <section className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {HOME_WATERS.map((w, i) => (
            <RiverCard key={w.name} water={w} index={i} />
          ))}
        </div>

        {/* CTA strip */}
        <div
          data-testid="home-waters-cta"
          className="mt-16 md:mt-20 surface-card rounded-md p-8 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-8"
        >
          <div className="flex items-center gap-6">
            <div
              className="flex-shrink-0 h-16 w-16 rounded-full border-2 border-[#13263c] flex items-center justify-center text-[#13263c]"
              aria-hidden="true"
            >
              <Waves size={28} />
            </div>
            <div>
              <h3 className="font-serif text-2xl md:text-3xl text-[#13263c] leading-tight">
                Good days start with good information.
              </h3>
              <p className="text-[#4a4a4a] mt-2 max-w-xl leading-relaxed">
                Check current flows, weather, and conditions before you go. Use our Planning Tools page for trusted resources and local updates.
              </p>
            </div>
          </div>
          <Link
            to="/planning-tools"
            data-testid="home-waters-cta-button"
            className="btn-primary self-start md:self-auto whitespace-nowrap"
          >
            View Planning Tools
          </Link>
        </div>
      </section>
    </div>
  );
}
